import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Slider from "react-slick";
import streetViewIcon from "../../../assets/img/street-view.png";
import vrGlassesIcon from "../../../assets/img/vr-glasses.png";
import { useAuth } from '../../../context/AuthContext';
import api from '../../../utils/api';
import { toast } from 'react-toastify';
interface ProjectHeroProps {
    project: any;
    getImageUrl: (url: string) => string;
    scrollToSection: (sectionId: string) => void;
    sliderSettings: any;
    openGallery: (images: string[], index: number) => void;
    addToCompare: (project: any) => void;
}

const ProjectHero: React.FC<ProjectHeroProps> = ({ project, getImageUrl, scrollToSection, sliderSettings, openGallery, addToCompare }) => {
    const heroImages = [
        ...(project.featuredImage ? [project.featuredImage] : []),
        ...(project.images || []),
        ...(project.gallery?.exterior || [])
    ].filter((img, index, self) => img && self.indexOf(img) === index); // Unique images only

    const { token, user } = useAuth();
    const navigate = useNavigate();
    const [isLiked, setIsLiked] = useState(false);
    const [isLiking, setIsLiking] = useState(false);

    const [nav1, setNav1] = React.useState<Slider | null>(null);
    const sliderRef1 = React.useRef<Slider | null>(null);
    const [thumbnailLimit, setThumbnailLimit] = React.useState(8);

    useEffect(() => {
        const checkLikeStatus = async () => {
            if (!token || !project._id) return;
            try {
                const data = await api.getWishlist(token);
                const combined = [
                    ...(data.wishlist || []),
                    ...(data.projectWishlist || [])
                ];
                setIsLiked(combined.some((item: any) => item._id === project._id));
            } catch (error) {
                console.error("Error checking like status:", error);
            }
        };
        checkLikeStatus();
    }, [token, project._id]);

    React.useEffect(() => {
        setNav1(sliderRef1.current);
        const updateLimit = () => {
            if (window.innerWidth < 768) {
                setThumbnailLimit(3);
            } else {
                setThumbnailLimit(8);
            }
        };
        updateLimit();
        window.addEventListener('resize', updateLimit);
        return () => window.removeEventListener('resize', updateLimit);
    }, []);

    const handleToggleLike = async () => {
        if (!token) {
            toast.info("Please login to save this project");
            navigate('/login');
            return;
        }

        if (isLiking) return;

        try {
            setIsLiking(true);
            const res = await api.toggleWishlist(token, project._id, 'project');
            setIsLiked(res.isSaved);
            toast.success(res.isSaved ? "Added to wishlist" : "Removed from wishlist");
        } catch (error) {
            console.error("Error toggling like:", error);
            toast.error("Failed to update wishlist");
        } finally {
            setIsLiking(false);
        }
    };

    const handleShare = async () => {
        const shareData = {
            title: project.name,
            text: `Check out this project on MyPropertyGuru: ${project.name}`,
            url: window.location.href,
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(window.location.href);
                toast.success("Link copied to clipboard!");
            }
        } catch (error) {
            console.error("Error sharing:", error);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <section className="p-0 relative project-hero-section">
            {/* Top Overlays */}
            <div className="hero-top-overlays absolute top-0 left-0 w-full p-4 md:px-[12%] md:py-6 flex justify-between items-start" style={{ zIndex: 20 }}>
                <div className="flex gap-2 items-center text-white">
                    <div className="hero-utility-btns hidden md:flex gap-3">
                        <button onClick={() => scrollToSection('gallery')} className="hero-util-btn bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white shadow-lg rounded-lg transition-all hover:scale-105 active:scale-95" title="View Gallery"><i className="fa-regular fa-image text-xl"></i></button>
                        <button onClick={() => scrollToSection('project-location')} className="hero-util-btn bg-white/20 backdrop-blur-md text-white shadow-lg rounded-lg transition-all hover:scale-105 active:scale-95 border border-white/30" title="Locality Presence"><i className="fa-solid fa-map text-xl"></i></button>
                        
                        <a 
                            href={project.mapDetails?.google3dViewLink || "#"} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="hero-util-btn bg-white/20 backdrop-blur-md text-white shadow-lg rounded-lg transition-all hover:scale-105 active:scale-95 flex items-center justify-center no-underline border border-white/30"
                            title="Google 3D View"
                            onClick={(e) => {
                                if (!project.mapDetails?.google3dViewLink) {
                                    e.preventDefault();
                                    alert("3D View not available for this project yet.");
                                }
                            }}
                        >
                            <img src={streetViewIcon} alt="Street View" className="w-7 h-7 object-contain brightness-0 invert" />
                        </a>

                        <button 
                            onClick={() => scrollToSection('media-tour')} 
                            className="hero-util-btn bg-white/20 backdrop-blur-md text-white shadow-lg rounded-lg transition-all hover:scale-105 active:scale-95 flex items-center justify-center border border-white/30"
                            title="360° Virtual Tour"
                        >
                            <img src={vrGlassesIcon} alt="VR View" className="w-10 h-10 object-contain brightness-0 invert" />
                        </button>

                        <button onClick={() => scrollToSection('media-tour')} className="hero-util-btn bg-white/20 backdrop-blur-md text-white shadow-lg rounded-lg transition-all hover:scale-105 active:scale-95 border border-white/30" title="Watch Video"><i className="fa-solid fa-video text-xl"></i></button>

                        <button 
                            onClick={() => addToCompare(project)} 
                            className="hero-util-btn bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white shadow-lg rounded-lg transition-all hover:scale-105 active:scale-95 group relative" 
                            title="Add to Compare"
                        >
                            <i className="fa-solid fa-plus-square text-xl"></i>
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full border border-white group-hover:scale-110 transition-transform"></div>
                        </button>
                    </div>
                </div>

                {/* Mobile Floating Actions */}
                <div className="md:hidden flex flex-col gap-2">
                    <button 
                        onClick={handleToggleLike}
                        disabled={isLiking}
                        className={`w-9 h-9 flex items-center justify-center backdrop-blur-md rounded-xl border border-white/20 shadow-lg active:scale-90 transition-all ${isLiked ? 'bg-rose-500 text-white' : 'bg-white/20 text-white'}`}
                    >
                        <i className={`fa-${isLiked ? 'solid' : 'regular'} fa-heart text-sm`}></i>
                    </button>
                    <button 
                        onClick={handleShare}
                        className="w-9 h-9 flex items-center justify-center bg-white/20 backdrop-blur-md text-white rounded-xl border border-white/20 shadow-lg active:scale-90 transition-all"
                    >
                        <i className="fa-solid fa-share-nodes text-sm"></i>
                    </button>
                </div>
            </div>

            <div className="project-gallery-slider relative">
                {(() => {
                    if (heroImages.length > 0) {
                        return (
                            <div className="relative">
                                {/* Main Image Slider */}
                                <Slider
                                    {...sliderSettings}
                                    ref={sliderRef1}
                                    className="main-hero-slider"
                                >
                                    {heroImages.map((img: string, i: number) => (
                                        <div key={i} className="gallery-slide">
                                            <div
                                                className="gallery-img-container mx-auto px-3 responsive-gallery-img"
                                                style={{
                                                    backgroundImage: `url(${getImageUrl(img)})`,
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center'
                                                }}
                                            >
                                            </div>
                                        </div>
                                    ))}
                                </Slider>
                            </div>
                        );
                    }

                    return (
                        <div className="gallery-placeholder" style={{ height: '600px', background: '#2c3e50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <h2 className="text-white opacity-50"><i className="fa-solid fa-image mr-2"></i>No Images Available</h2>
                        </div>
                    );
                })()}

                <div className="hidden md:hidden absolute bottom-6 right-4 gap-2 z-20">
                    <button onClick={() => scrollToSection('gallery')} className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-full shadow-lg"><i className="fa-regular fa-image text-sm"></i></button>
                    <button onClick={() => scrollToSection('project-location')} className="w-10 h-10 flex items-center justify-center bg-[#001b4a]/80 backdrop-blur-sm text-white rounded-full shadow-lg"><i className="fa-solid fa-map text-sm"></i></button>
                    <button onClick={() => scrollToSection('media-tour')} className="w-10 h-10 flex items-center justify-center bg-[#001b4a]/80 backdrop-blur-sm text-white rounded-full shadow-lg"><i className="fa-solid fa-video text-sm"></i></button>
                </div>
            </div>

            {/* Bottom Overlays */}
            <div className="project-hero-info absolute bottom-0 left-0 w-full p-4 md:p-8" style={{ zIndex: 10 }}>
                <div className="w-full">
                    <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-2 md:gap-0">
                        <div className="w-full md:w-auto text-white px-3 md:px-[12%]">
                            <nav className="hero-breadcrumb mb-6 hidden md:block text-shadow-heavy">
                                <div className="inline-flex items-center gap-2 text-white bg-white/20 backdrop-blur-md px-6 py-1 rounded-full text-sm border border-white/30">
                                    <Link to="/" className="text-white text-decoration-none opacity-75 hover:opacity-100 transition-opacity"><i className="fa-solid fa-house mr-1"></i> Home</Link>
                                    <span className="opacity-50 text-[0.65rem]"><i className="fa-solid fa-chevron-right"></i></span>
                                    <span className="opacity-75">{project.propertyType || 'Property'}</span>
                                    <span className="opacity-50 text-[0.65rem]"><i className="fa-solid fa-chevron-right"></i></span>
                                    <span className="font-bold">{project.name}</span>
                                </div>
                            </nav>

                            <h1 className="font-extrabold text-2xl md:text-4xl lg:text-5xl mb-1 md:mb-2 text-shadow-heavy text-white hidden md:block" style={{ color: '#ffffff' }}>{project.name}</h1>

                             {project.developer?.name && (
                                <div className="mb-3 hidden md:block">
                                    <Link 
                                        to={project.developer?.role === 'broker' ? `/agent-page/${project.developer._id}` : `/developer-page/${project.developer._id}`} 
                                        className="text-white text-shadow-heavy opacity-100 hover:text-accent transition-all text-sm md:text-base font-bold no-underline inline-flex items-center gap-2 group"
                                    >
                                        <span className="group-hover:opacity-100 font-medium">By</span>
                                        <span className="group-hover:text-accent font-bold">{project.developer.name}</span>
                                        <i className="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-0 group-hover:opacity-100 transition-all -translate-y-0.5"></i>
                                    </Link>
                                </div>
                            )}



                            <p className="text-sm md:text-lg lg:text-xl mb-1 opacity-100 text-shadow-heavy hidden md:flex items-center gap-2">
                                <i className="fa-solid fa-location-dot text-accent"></i>
                                {project.address || project.location}
                            </p>

                            <p className="text-sm md:text-lg lg:text-xl mb-0 opacity-100 text-shadow-heavy hidden md:flex items-center gap-2">
                                <i className="fa-solid fa-fingerprint text-accent"></i>
                                Project ID: Pr-{project._id?.substring(project._id.length - 4)}
                                {project.reraNumber && (
                                    <span className="inline-flex items-center gap-1.5 bg-white/95 px-2 py-0.5 rounded border border-gray-200 shadow-sm ml-2 text-shadow-none">
                                        <i className="fa-solid fa-check text-emerald-500 text-[10px]"></i>
                                        <span className="text-[10px] md:text-xs font-bold text-slate-800 tracking-wide">Rera</span>
                                    </span>
                                )}
                            </p>
                        </div>

                        <div className="hidden md:block w-auto lg:w-1/3 px-3 md:px-[12%] text-right">
                            <div className="hero-action-suite hidden md:flex justify-content-lg-end gap-2 mb-6">
                                <button 
                                    onClick={handleToggleLike}
                                    disabled={isLiking}
                                    className={`inline-block text-center cursor-pointer transition-all btn-outline-white rounded-sm hero-action-btn ${isLiked ? 'bg-rose-600 border-rose-600 text-white' : ''}`}
                                    title={isLiked ? "Remove from Wishlist" : "Add to Wishlist"}
                                >
                                    <i className={`fa-${isLiked ? 'solid' : 'regular'} fa-heart`}></i>
                                </button>
                                <button 
                                    onClick={handleShare}
                                    className="inline-block text-center cursor-pointer transition-all btn-outline-white rounded-sm hero-action-btn"
                                    title="Share"
                                >
                                    <i className="fa-solid fa-share-nodes"></i>
                                </button>
                                <button 
                                    onClick={handlePrint}
                                    className="inline-block text-center cursor-pointer transition-all btn-outline-white rounded-sm hero-action-btn"
                                    title="Print"
                                >
                                    <i className="fa-solid fa-print"></i>
                                </button>
                            </div>

                            <div className="price-display text-white text-shadow-2xl md:mt-0">
                                <h4 className="font-extrabold mb-1 text-white text-2xl md:text-[1.85rem]" style={{ lineHeight: '1.3' }}>
                                    {project.overview?.priceRange || project.priceRange || 'On Request'}
                                </h4>
                                <p className="opacity-80 mb-0 text-xs md:text-sm font-medium hidden md:block">
                                    {project.overview?.avgPricePerSqFt ? `${project.overview.avgPricePerSqFt}/sq ft` : 'Price on Request'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProjectHero;
